import { Bolt, CloudUpload, Draw, HighlightAlt, SvgIconComponent } from "@mui/icons-material";

import { ADD_BOUNDARY_FIRST } from "../copy";
import type { BaseDataGating } from "../gating";
import { BASE_DATA_LAYERS, BaseDataLayerKey, BaseDataOption, isGenerationKey } from "../layers";
import { BaseDataTask, BaseDataTaskKind, getTaskKindOption } from "../tasks";

/** Display order of a base data row's options. */
export const BASE_DATA_OPTION_ORDER: readonly BaseDataOption[] = ["importWithinBoundary", "generate", "importLayer", "upload", "draw"];

/** An option offered in a base data row's flyout. */
export type BaseDataOptionItem = {
  id: BaseDataOption;
  icon: SvgIconComponent;
  title: string;
  /** Subtitle, or why the option can't be used when it is disabled. */
  subtitle: string;
  disabled: boolean;
};

export type BaseDataOptionsInput = {
  /** Whether the row has data in any of its planner layers. */
  hasData: boolean;
  gating: Pick<BaseDataGating, "generate" | "generatedFrom" | "rows">;
  /** The task started from the row's flyout, if any. */
  task?: BaseDataTask | null;
};

/** Return the option showing a task's progress on a row; the Boundary row shows its imports under Import FOND base data. */
const getTaskOption = (key: BaseDataLayerKey, kind: BaseDataTaskKind): BaseDataOption | null =>
  key === "boundary" && kind === "boundaryImport" ? "importWithinBoundary" : getTaskKindOption(kind);

const getOption = (key: BaseDataLayerKey, id: BaseDataOption, { hasData, gating }: BaseDataOptionsInput): BaseDataOptionItem => {
  const { name, shortName } = BASE_DATA_LAYERS[key];
  const layerName = shortName ?? name.toLowerCase();
  const isBoundary = key === "boundary";

  switch (id) {
    case "generate": {
      const gate = isGenerationKey(key) ? gating.generate[key] : null;
      const subtitle = isGenerationKey(key) ? gating.generatedFrom[key] : "";
      return { id, icon: Bolt, title: "Generate", subtitle: gate?.reason ?? subtitle, disabled: !gate?.enabled };
    }
    case "importWithinBoundary":
      return {
        id,
        icon: HighlightAlt,
        title: "Import FOND base data",
        subtitle: hasData ? "Within this boundary" : ADD_BOUNDARY_FIRST,
        disabled: !hasData,
      };
    case "importLayer":
      return { id, icon: HighlightAlt, title: `Import ${layerName}`, subtitle: "Use FOND data", disabled: false };
    case "upload":
      return { id, icon: CloudUpload, title: `Upload ${layerName}`, subtitle: "Use my data", disabled: false };
    case "draw": {
      const boundarySubtitle = hasData ? "Edit the boundary polygon" : "Draw the boundary polygon";
      return { id, icon: Draw, title: "Draw on map", subtitle: isBoundary ? boundarySubtitle : "Add or edit features on the map", disabled: false };
    }
    default:
      throw new Error(`Unknown base data option: ${id satisfies never}`);
  }
};

/** Return the options a base data row offers, in display order, disabled while the row's data can't change. */
export const getBaseDataOptions = (key: BaseDataLayerKey, input: BaseDataOptionsInput): BaseDataOptionItem[] => {
  const { options } = BASE_DATA_LAYERS[key];
  const { change } = input.gating.rows[key];
  const taskOption = input.task?.status === "running" ? getTaskOption(key, input.task.kind) : null;
  return BASE_DATA_OPTION_ORDER.filter((id) => options.includes(id))
    .map((id) => getOption(key, id, input))
    .map((option) => (change.enabled || option.id === taskOption ? option : { ...option, disabled: true }));
};

/** Return the heading above a base data row's options. */
export const getBaseDataOptionsHeading = (key: BaseDataLayerKey, hasData: boolean): string => {
  if (key === "boundary") return hasData ? "Use or replace the boundary" : "Add a boundary";
  return `${hasData ? "Replace or edit" : "Add"} ${BASE_DATA_LAYERS[key].noun}`;
};
